import { CSSProperties, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  SelectedTab,
  selectSelectedTab,
} from "../../app/slices/GlobalSlice";
import {
  selectIsPlaying,
  selectTrainingType,
  TrainingType,
} from "../../app/slices/TrainingSlice";
import { selectIsConnectedToWebSocket } from "../../app/slices/WebSocketSlice";
import ValidationStatusPill from "../validation/ValidationStatusPill";
import LocalizationGroup from "./LocalizationGroup";
import SearchTriggerPill from "./SearchTriggerPill";
import SendToUnityButton from "./SendToUnityButton";
import TestTrainingSplitButton from "./TestTrainingSplitButton";
import "./TrainingActionbar.css";
import TrainingActionbarConnectButton from "./TrainingActionbarConnectButton";
import TrainingActionbarSaveButton from "./TrainingActionbarSaveButton";
import TrainingActionbarTitle from "./TrainingActionbarTitle";
import UndoRedoGroup from "./UndoRedoGroup";
import PiHelpButton from "../onboarding/PiHelpButton";

// Widths of the layout columns flanking the viewport, used to center the test
// cluster over it. Mirror the values in:
//   3D : ScenarioBuilderTabRail (w-12 = 48), ScenarioBuilderColumn (250),
//        StoryboardTimeline3D (250, Timeline tab only),
//        .trainingeditor-3d-properties (250)
//   360: TrainingEditor360Rail (w-12 = 48), TrainingTimeline360Content (500),
//        360 PropertiesColumn (250)
const TAB_RAIL_WIDTH = 48;
const SCENARIO_COLUMN_WIDTH = 250;
const STORYBOARD_TIMELINE_WIDTH = 250;
const PROPERTIES_3D_WIDTH = 250;
const TIMELINE_360_WIDTH = 500;
const PROPERTIES_360_WIDTH = 250;

export default function TrainingActionbar() {
  const trainingType = useSelector(selectTrainingType);
  const is3D = trainingType === TrainingType.Training3D;
  const isConnected = useSelector(selectIsConnectedToWebSocket);
  const isPlaying = useSelector(selectIsPlaying);
  const selectedTab = useSelector(selectSelectedTab);

  const testButton = useMemo(() => {
    return isConnected ? <SendToUnityButton /> : <TestTrainingSplitButton />;
  }, [isConnected]);

  // Track the actual viewport bounds so the test cluster can sit centered
  // above it, accounting for whichever side panels are currently shown.
  const viewportLeftPx = isPlaying
    ? 0
    : is3D
    ? TAB_RAIL_WIDTH +
      (selectedTab === SelectedTab.Timeline ? STORYBOARD_TIMELINE_WIDTH : 0) +
      SCENARIO_COLUMN_WIDTH
    : TAB_RAIL_WIDTH + TIMELINE_360_WIDTH;
  const viewportRightPx = isPlaying
    ? 0
    : is3D
    ? PROPERTIES_3D_WIDTH
    : PROPERTIES_360_WIDTH;

  const rootStyle = {
    "--actionbar-viewport-left": `${viewportLeftPx}px`,
    "--actionbar-viewport-right": `${viewportRightPx}px`,
  } as CSSProperties;

  return (
    <div
      className={`trainingactionbar-root${isPlaying ? " is-playing" : ""}`}
      style={rootStyle}
    >
      <div className="trainingactionbar-cluster trainingactionbar-left-cluster">
        <div className="trainingactionbar-project-group">
          <TrainingActionbarTitle />
          <TrainingActionbarSaveButton />
        </div>
        <span className="trainingactionbar-divider" />
        <UndoRedoGroup />
        {!isPlaying && (
          <div className="trainingactionbar-inspect">
            <SearchTriggerPill />
            <ValidationStatusPill />
          </div>
        )}
      </div>
      <div className="trainingactionbar-test-cluster">
        {testButton}
        <TrainingActionbarConnectButton />
      </div>
      <div className="trainingactionbar-right-cluster">
        {/* Top right, beside the language controls: same place as on the
            dashboard. 360 is the exception — it carries the same action in
            its rail, above Exit Editor, so mounting it here too would offer
            the tour twice on one screen. */}
        {!isPlaying && is3D && <PiHelpButton testId="editor-help" />}
        <LocalizationGroup />
      </div>
    </div>
  );
}
